// components/AddSupplierModal.tsx
import React, { useEffect, useMemo, useState } from 'react';
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Form,
  Label,
  Spinner,
  Alert,
} from 'reactstrap';
import Select from 'react-select';
import { useFormik } from 'formik';
import * as Yup from 'yup';

// RTK Query import
import { useGetSuppliersQuery } from 'slices/InventoryManagement/inventoryManagementSlice';

interface Supplier {
  id: number;
  name: string;
  contact_name: string;
  contact_email: string;
  contact_phone: string;
}

interface SupplierCostMapping {
  mapping_id: number;
  supplier_id: number;
  supplier_name: string;
  supplier_email: string | null;
  supplier_phone: string | null;
  total_amount: number;
  discount: number;
}

interface BulkSupplierMapping {
  supplier: number[];
  product: number;
  total_amount: number;
  discount: number;
}

interface AddSupplierModalProps {
  isOpen: boolean;
  toggle: () => void;
  productId: number;
  onSubmit: (data: BulkSupplierMapping) => Promise<void>;
  isLoading: boolean;
  error?: any;
  existingSuppliers: SupplierCostMapping[] | any;
}

/** Helper to safely extract an array from any API response */
const extractArray = (data: any): any[] => {
  if (!data) return [];
  if (Array.isArray(data)) return data;
  if (Array.isArray(data.results)) return data.results;
  if (Array.isArray(data.suppliers)) return data.suppliers;
  return [];
};

const AddSupplierModal: React.FC<AddSupplierModalProps> = ({
  isOpen,
  toggle,
  productId,
  onSubmit,
  isLoading,
  error,
  existingSuppliers,
}) => {
  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const pageSize = 20;

  useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedSearch(searchInput.trim()), 350);
    return () => window.clearTimeout(timer);
  }, [searchInput]);

  const { data: suppliersData, isLoading: suppliersLoading } = useGetSuppliersQuery(
    { page: 1, pageSize, search: debouncedSearch },
    { skip: !isOpen || debouncedSearch.length < 2 }
  );
  const [selectedSuppliers, setSelectedSuppliers] = useState<any[]>([]);

  /** Safely get supplier IDs already mapped to this product */
  const mappedSupplierIds = useMemo(() => {
    if (!Array.isArray(existingSuppliers)) return [];
    return existingSuppliers.map((supplier) => supplier.supplier_id);
  }, [existingSuppliers]);

  /** Safely extract supplier list from API response */
  const allSuppliers = useMemo(() => extractArray(suppliersData), [suppliersData]);

  /** Build select options for react-select */
  const supplierOptions = useMemo(() => {
    return allSuppliers.map((supplier: any) => ({
      value: supplier.supplier_id || supplier.id,
      label:
        `${supplier.supplier_name || supplier.name}` +
        (supplier.contact_name ? ` - ${supplier.contact_name}` : '') +
        (supplier.contact_email ? ` (${supplier.contact_email})` : ''),
      isDisabled: mappedSupplierIds.includes(supplier.supplier_id || supplier.id),
    }));
  }, [allSuppliers, mappedSupplierIds]);

  const availableSuppliers = useMemo(
    () => supplierOptions.filter((option) => !option.isDisabled),
    [supplierOptions]
  );

  /** Formik setup */
  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      suppliers: [] as number[],
      total_amount: '',
      discount: '0',
    },
    validationSchema: Yup.object({
      suppliers: Yup.array()
        .of(Yup.number())
        .min(1, 'Please select at least one supplier')
        .required('Please select suppliers'),
    }),
    onSubmit: async (values) => {
      await onSubmit({
        supplier: values.suppliers,
        product: productId,
        total_amount: Number(values.total_amount),
        discount: Number(values.discount),
      });

      validation.resetForm();
      setSelectedSuppliers([]);
    },
  });

  /** Handle supplier selection */
  const handleSuppliersChange = (selectedOptions: any) => {
    setSelectedSuppliers(selectedOptions || []);
    validation.setFieldTouched('suppliers', true, false);
    validation.setFieldValue(
      'suppliers',
      selectedOptions ? selectedOptions.map((option: any) => option.value) : []
    );
  };

  /** Handle modal close/reset */
  const handleClose = () => {
    validation.resetForm();
    setSelectedSuppliers([]);
    setSearchInput('');
    setDebouncedSearch('');
    toggle();
  };

  const formatSupplierOption = (option: any) => {
    const supplier = allSuppliers.find(
      (item: any) => (item.supplier_id || item.id) === option.value
    );
    return (
      <div>
        <div>{supplier?.supplier_name || supplier?.name || option.label}</div>
        <small className="text-muted">
          {[supplier?.contact_name, supplier?.contact_email, supplier?.contact_phone]
            .filter(Boolean)
            .join(' | ') || 'No contact details'}
        </small>
      </div>
    );
  };

  return (
    <Modal isOpen={isOpen} toggle={handleClose} size="lg">
      <ModalHeader toggle={handleClose}>Add Suppliers for Product</ModalHeader>
      <Form
        onSubmit={(e) => {
          e.preventDefault();
          validation.handleSubmit();
        }}
      >
        <ModalBody>
          {error && (
            <Alert color="danger">{error?.data?.message || 'Failed to add suppliers'}</Alert>
          )}

          <div className="mb-3">
            <Label htmlFor="suppliers-select" className="form-label">
              Search and select suppliers <span className="text-danger">*</span>
            </Label>
            <Select
              id="suppliers-select"
              value={selectedSuppliers}
              onChange={handleSuppliersChange}
              onInputChange={(value: string, action: { action: string }) => {
                if (action.action === 'input-change') setSearchInput(value);
              }}
              options={availableSuppliers}
              formatOptionLabel={formatSupplierOption}
              placeholder="Type at least 2 characters to search suppliers..."
              isMulti
              isSearchable
              isLoading={suppliersLoading}
              isDisabled={isLoading}
              classNamePrefix="select"
              closeMenuOnSelect={false}
              hideSelectedOptions={false}
              noOptionsMessage={() =>
                searchInput.trim().length < 2
                  ? 'Type at least 2 characters to search.'
                  : suppliersLoading
                    ? 'Loading suppliers...'
                    : 'No matching suppliers found.'
              }
            />
            {validation.errors.suppliers && validation.touched.suppliers && (
              <div className="text-danger" style={{ fontSize: '0.875em' }}>
                {validation.errors.suppliers as string}
              </div>
            )}
            <div className="form-text">Search by supplier name, contact, email, or phone number.</div>
          </div>

          {/* Selected Summary */}
          {selectedSuppliers.length > 0 && (
            <div className="alert alert-info">
              <strong>Selected Suppliers:</strong> {selectedSuppliers.length}
              <ul className="mb-0 mt-1">
                {selectedSuppliers.map((supplier) => (
                  <li key={supplier.value}>{supplier.label}</li>
                ))}
              </ul>
            </div>
          )}
        </ModalBody>

        <ModalFooter>
          <Button color="secondary" onClick={handleClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button
            color="primary"
            type="submit"
            disabled={isLoading || !validation.isValid || selectedSuppliers.length === 0}
          >
            {isLoading ? (
              <>
                <Spinner size="sm" className="me-2" />
                Adding Suppliers...
              </>
            ) : (
              `Add ${selectedSuppliers.length} Supplier(s)`
            )}
          </Button>
        </ModalFooter>
      </Form>
    </Modal>
  );
};

export default AddSupplierModal;
